/**
 * Root CSRF/credentials shim for hand-rolled HTTP.
 *
 * The axios client (lib/api.ts) already attaches the in-memory Bearer token, the
 * `X-CSRF-Token` double-submit header, and `withCredentials` on every request.
 * But the app also makes MANY raw `fetch()` / `XMLHttpRequest` calls (uploads,
 * downloads, presigned flows) that bypass axios — after the cookie+CSRF auth
 * migration those cookie-authenticated mutations were rejected with `403 CSRF
 * token invalid`.
 *
 * Rather than patch each call site (fragile — the next raw request forgets it),
 * this installs ONE global wrapper around `window.fetch` and `XMLHttpRequest`
 * so EVERY mutating request TO OUR API ORIGIN automatically carries:
 *   - `credentials: 'include'` (send the httpOnly auth cookie),
 *   - `X-CSRF-Token` (the readable csrf_token cookie), and
 *   - `Authorization: Bearer <in-memory token>` when one isn't already set.
 *
 * Requests to any OTHER origin (S3 presigned PUTs, CloudFront, third parties)
 * are left completely untouched — adding these headers there would break the
 * S3 signature / trigger CORS failures.
 *
 * Import this once, before the app renders.
 */
import { API_BASE_URL, getAccessToken, getCsrfToken } from './api';

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** True only for requests aimed at our own backend API. */
function isOurApi(url: string): boolean {
  if (!url) return false;
  try {
    // Absolute API base (prod + dev both use an absolute REACT_APP_API_URL).
    if (url.startsWith(API_BASE_URL)) return true;
    // Defensive: an absolute URL whose origin+path starts with the API base's.
    const u = new URL(url, window.location.origin);
    const base = new URL(API_BASE_URL, window.location.origin);
    return u.origin === base.origin && u.pathname.startsWith(base.pathname);
  } catch {
    return false;
  }
}

export function installHttpCsrf(): void {
  const w = window as any;
  if (w.__httpCsrfInstalled) return;
  w.__httpCsrfInstalled = true;

  // ── fetch ────────────────────────────────────────────────────────────────
  const origFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init: RequestInit = {}) => {
    try {
      const url = typeof input === 'string' ? input : (input instanceof URL ? input.href : (input as Request).url);
      const method = (init.method || (typeof input !== 'string' && !(input instanceof URL) ? (input as Request).method : 'GET') || 'GET').toUpperCase();
      if (isOurApi(url) && MUTATING.has(method)) {
        init = { ...init };
        if (!init.credentials) init.credentials = 'include';
        const headers = new Headers(init.headers || (typeof input !== 'string' && !(input instanceof URL) ? (input as Request).headers : undefined) || {});
        if (!headers.has('X-CSRF-Token')) { const c = getCsrfToken(); if (c) headers.set('X-CSRF-Token', c); }
        if (!headers.has('Authorization')) { const t = getAccessToken(); if (t) headers.set('Authorization', `Bearer ${t}`); }
        init.headers = headers;
      }
    } catch { /* never break a request */ }
    return origFetch(input as any, init);
  };

  // ── XMLHttpRequest ───────────────────────────────────────────────────────
  const XHR = XMLHttpRequest.prototype;
  const origOpen = XHR.open;
  const origSend = XHR.send;
  const origSetHeader = XHR.setRequestHeader;

  XHR.open = function (this: any, method: string, url: string, ...rest: any[]) {
    this.__csrfMethod = String(method || '').toUpperCase();
    this.__csrfUrl = String(url || '');
    this.__csrfHasAuth = false;
    this.__csrfHasToken = false;
    return origOpen.call(this, method as any, url as any, ...(rest as [boolean]));
  };

  XHR.setRequestHeader = function (this: any, name: string, value: string) {
    const n = String(name).toLowerCase();
    if (n === 'authorization') this.__csrfHasAuth = true;
    if (n === 'x-csrf-token') this.__csrfHasToken = true;
    return origSetHeader.call(this, name, value);
  };

  XHR.send = function (this: any, body?: any) {
    try {
      if (isOurApi(this.__csrfUrl) && MUTATING.has(this.__csrfMethod)) {
        this.withCredentials = true;
        if (!this.__csrfHasToken) { const c = getCsrfToken(); if (c) origSetHeader.call(this, 'X-CSRF-Token', c); }
        if (!this.__csrfHasAuth) { const t = getAccessToken(); if (t) origSetHeader.call(this, 'Authorization', `Bearer ${t}`); }
      }
    } catch { /* never break a request */ }
    return origSend.call(this, body);
  };
}
